import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Search, Plus, Zap, Users, Calendar, MoreVertical,
  AlertTriangle, Play, Construction, Pencil, Trash2,
  LayoutGrid as GridIcon, List, Clock, Trophy, Star, User2, AlertCircle,
  Target, CheckCircle, TrendingUp, FileText, ClipboardList, X, SlidersHorizontal,
  Layers, Timer, Power, PowerOff,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Legend, LineChart, Line, CartesianGrid,
  AreaChart, Area,
} from 'recharts';

import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ProgressBar } from '@/components/shared/ProgressBar';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth.store';
import { goalService, GOAL_STATUSES, GOAL_PRIORITIES, type Goal } from '@/services/goal.service';
import { sprintService, type Sprint } from '@/services/sprint.service';
import { groupService } from '@/services/group.service';
import { teamService, type Team } from '@/services/team.service';
import { CreateSprintDialog } from '@/components/sprints/CreateSprintDialog';
import { cn } from '@/lib/utils';

const TABS = [
  { value: 'goals-list', label: 'Goals List' },
  { value: 'goals-dashboard', label: 'Goals Dashboard' },
  { value: 'sprints-list', label: 'Sprints List' },
  { value: 'sprints-dashboard', label: 'Sprints Dashboard' },
  { value: 'teams-list', label: 'Teams List' },
  { value: 'teams-dashboard', label: 'Teams Dashboard' },
];

// ===========================================================================
// Work Planning Page
// ===========================================================================
export function WorkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'goals-list';

  function setTab(tab: string) {
    setSearchParams({ tab });
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Work" description="Manage your goals, sprints, and teams in one place" />

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs sm:text-sm">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="goals-list" className="mt-4"><GoalsListTab /></TabsContent>
        <TabsContent value="goals-dashboard" className="mt-4"><GoalsDashboardTab /></TabsContent>
        <TabsContent value="sprints-list" className="mt-4"><SprintsListTab /></TabsContent>
        <TabsContent value="sprints-dashboard" className="mt-4"><SprintsDashboardTab /></TabsContent>
        <TabsContent value="teams-list" className="mt-4"><TeamsListTab /></TabsContent>
        <TabsContent value="teams-dashboard" className="mt-4"><TeamsDashboardTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===========================================================================
// Helpers — status / priority pill renderers for table & cards
// ===========================================================================
const STATUS_PILL_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  completed_late: 'bg-orange-50 text-orange-700',
  cancelled: 'bg-red-50 text-red-700',
};
const PRIORITY_PILL_STYLES: Record<string, string> = {
  low: 'bg-green-50 text-green-700',
  medium: 'bg-yellow-50 text-yellow-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
};

function StatusPill({ status }: { status: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', STATUS_PILL_STYLES[status] ?? 'bg-gray-100 text-gray-600')}>
      {status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

function PriorityPill({ priority }: { priority: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', PRIORITY_PILL_STYLES[priority] ?? 'bg-gray-100 text-gray-600')}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
}

// Helper: progress bar color by value
function progressColor(v: number) {
  if (v >= 75) return 'bg-green-500';
  if (v >= 25) return 'bg-yellow-500';
  return 'bg-red-500';
}

function overdueDays(dueDateStr: string | null | undefined): number {
  if (!dueDateStr) return 0;
  const diff = Date.now() - new Date(dueDateStr).getTime();
  return diff > 0 ? Math.ceil(diff / 86400000) : 0;
}

// ===========================================================================
// TAB 1 — Goals List
// ===========================================================================
function GoalsListTab() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const fromTab = sp.get('tab') || 'goals-list';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const user = useAuthStore((s) => s.user);
  const { isAdmin, activeWorkspaceRole } = useAuth();
  const isWsAdmin = isAdmin || activeWorkspaceRole === 'owner' || activeWorkspaceRole === 'admin';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createPriority, setCreatePriority] = useState('medium');
  const [createDueDate, setCreateDueDate] = useState('');
  const [createGroupId, setCreateGroupId] = useState('');
  const [createOwnerId, setCreateOwnerId] = useState(user?.id ?? '');
  const [createTags, setCreateTags] = useState<string[]>([]);
  const [createTagInput, setCreateTagInput] = useState('');
  const [createDepIds, setCreateDepIds] = useState<string[]>([]);
  const [createEvalIds, setCreateEvalIds] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const hasFilters = !!statusFilter || !!priorityFilter || !!groupFilter || !!ownerFilter;
  const activeFilterCount = [statusFilter, priorityFilter, groupFilter, ownerFilter].filter(Boolean).length;

  const params = {
    cursor: cursors[page], limit: 20, sort: 'created_at' as const, order: 'desc' as const,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
    ...(groupFilter && { group_id: groupFilter }),
    ...(ownerFilter && { owner_id: ownerFilter }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['work-goals', params],
    queryFn: () => goalService.list(params).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups-for-select'],
    queryFn: () => groupService.list({ limit: 100 }).then((r) => r.data.data),
  });

  // Load all goals (unfiltered) to extract unique owners for the filter dropdown
  const { data: allGoalsData } = useQuery({
    queryKey: ['all-goals-owners'],
    queryFn: () => goalService.list({ limit: 100 }).then((r) => r.data.data),
  });

  const uniqueOwners = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const g of (allGoalsData ?? [])) {
      if (g.owner && !map.has(g.owner.id)) {
        map.set(g.owner.id, { id: g.owner.id, name: g.owner.full_name });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allGoalsData]);

  // Dependent on selected group (for create dialog)
  const { data: groupMembers } = useQuery({
    queryKey: ['group-members-select', createGroupId],
    queryFn: () => groupService.listMembers(createGroupId, { limit: 100 }).then((r) => r.data.data),
    enabled: !!createGroupId,
  });

  const { data: groupGoals } = useQuery({
    queryKey: ['group-goals-select', createGroupId],
    queryFn: () => goalService.list({ group_id: createGroupId, limit: 100 }).then((r) => r.data.data),
    enabled: !!createGroupId,
  });

  const goals = data?.data ?? [];
  const pagination = data?.pagination;

  function resetCreateForm() {
    setCreateTitle(''); setCreateDesc(''); setCreatePriority('medium'); setCreateDueDate('');
    setCreateGroupId(''); setCreateOwnerId(user?.id ?? '');
    setCreateTags([]); setCreateTagInput(''); setCreateDepIds([]); setCreateEvalIds([]);
    setFormErrors({});
  }

  function validateGoalForm() {
    const errors: Record<string, string> = {};
    if (!createTitle.trim()) errors.title = 'Title is required';
    if (!createGroupId) errors.group = 'Group is required';
    if (!createOwnerId) errors.owner = 'Owner is required';
    if (!createDueDate) errors.due_date = 'Due date is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const createMut = useMutation({
    mutationFn: () => goalService.create({
      title: createTitle,
      description: createDesc || undefined,
      priority: createPriority,
      due_date: createDueDate,
      group_id: createGroupId,
      owner_id: createOwnerId,
      evaluator_ids: createEvalIds.length > 0 ? createEvalIds : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-goals'] });
      toast({ title: 'Goal created' }); setCreateOpen(false);
      resetCreateForm();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function resetPage() { setPage(0); setCursors([undefined]); }

  function clearFilters() {
    setStatusFilter(''); setPriorityFilter(''); setGroupFilter(''); setOwnerFilter('');
    resetPage();
  }

  const deleteMut = useMutation({
    mutationFn: (id: string) => goalService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-goals'] });
      toast({ title: 'Goal deleted' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const columns: Column<Goal>[] = [
    { key: 'reference_id', header: 'ID', className: 'w-[110px]',
      render: (r) => <span className="font-mono text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{r.reference_id}</span> },
    { key: 'title', header: 'Title', sortable: true,
      render: (r) => <Link to={`/goals/${r.id}`} className="text-sm font-medium text-gray-900 hover:text-purple-600 cursor-pointer" onClick={(e) => e.stopPropagation()}>{r.title}</Link> },
    { key: 'group', header: 'Group', hideable: true,
      render: (r) => <span className="text-sm text-gray-500">{r.group?.name ?? '—'}</span> },
    { key: 'owner', header: 'Owner', hideable: true,
      render: (r) => r.owner ? (
        <div className="flex items-center gap-1.5">
          <UserAvatar name={r.owner.full_name} avatarUrl={r.owner.avatar_url} size="xs" />
          <span className="text-sm text-gray-700">{r.owner.full_name}</span>
        </div>
      ) : <span className="text-sm text-gray-400">—</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
    { key: 'priority', header: 'Priority', sortable: true, render: (r) => <PriorityPill priority={r.priority} /> },
    { key: 'due_date', header: 'Due Date', sortable: true,
      render: (r) => {
        const od = r.status === 'active' ? overdueDays(r.due_date) : 0;
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-500">{r.due_date ? new Date(r.due_date).toLocaleDateString() : '—'}</span>
            {od > 0 && <span className="rounded-full bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold">{od}d overdue</span>}
          </div>
        );
      } },
    { key: 'progress', header: 'Progress', className: 'w-[160px]',
      render: (r) => {
        const v = r.progress_percentage ?? 0;
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div className={cn('h-full rounded-full transition-all', progressColor(v))} style={{ width: `${v}%` }} />
            </div>
            <span className="text-xs text-gray-500 w-8 text-right">{v}%</span>
          </div>
        );
      } },
    { key: 'actions', header: '', className: 'w-[80px]',
      render: (r) => {
        const canAct = isWsAdmin || r.owner_id === user?.id;
        if (!canAct) return null;
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
              <Link to={`/goals/${r.id}`}><Pencil className="h-3.5 w-3.5" /></Link>
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => deleteMut.mutate(r.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      } },
  ];

  return (
    <div className="space-y-4">
      {/* Toolbar: search + filters toggle + view toggle + create */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search goals..." value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }} className="pl-9" />
        </div>
        <div className="flex items-center gap-1 rounded-md border p-0.5">
          <Button variant="ghost" size="sm" className={cn('h-7 w-7 p-0', viewMode === 'grid' && 'bg-muted')} aria-label="Card view" onClick={() => setViewMode('grid')}>
            <GridIcon className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className={cn('h-7 w-7 p-0', viewMode === 'list' && 'bg-muted')} aria-label="Table view" onClick={() => setViewMode('list')}>
            <List className="h-4 w-4" />
          </Button>
        </div>
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1.5"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </Button>
        <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Create Goal
        </Button>
      </div>

      {/* Filter dropdowns — hidden by default, toggled by Filters button */}
      {showFilters && (
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
        >
          <option value="">All Statuses</option>
          {GOAL_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => { setPriorityFilter(e.target.value); resetPage(); }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
        >
          <option value="">All Priorities</option>
          {GOAL_PRIORITIES.map((p) => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <select
          value={groupFilter}
          onChange={(e) => { setGroupFilter(e.target.value); resetPage(); }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
        >
          <option value="">All Groups</option>
          {(groupsData ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select
          value={ownerFilter}
          onChange={(e) => { setOwnerFilter(e.target.value); resetPage(); }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
        >
          <option value="">All Owners</option>
          {uniqueOwners.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        {hasFilters && (
          <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>
      )}

      {/* Grid Card View */}
      {viewMode === 'grid' ? (
        isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
          </div>
        ) : goals.length === 0 ? (
          <EmptyState icon={Building2} title="No goals found" description="Create a goal to get started." />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {goals.map((goal) => <GoalCard key={goal.id} goal={goal} fromTab={fromTab} />)}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={!pagination?.has_more} onClick={() => {
                if (pagination?.cursor) {
                  setCursors((p) => { const n = [...p]; n[page + 1] = pagination.cursor!; return n; });
                  setPage((p) => p + 1);
                }
              }}>Next</Button>
            </div>
          </>
        )
      ) : (
        <DataTable
          columns={columns}
          data={goals}
          isLoading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/goals/${r.id}`, { state: { fromTab } })}
          emptyTitle="No goals found"
          pagination={{
            hasNext: !!pagination?.has_more, hasPrev: page > 0,
            onNext: () => { if (pagination?.cursor) { setCursors((p) => { const n = [...p]; n[page + 1] = pagination.cursor!; return n; }); setPage((p) => p + 1); } },
            onPrev: () => setPage((p) => Math.max(0, p - 1)),
          }}
        />
      )}

      {/* Create Goal Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetCreateForm(); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>Create Goal</DialogTitle>
            <DialogDescription>Add a new goal to your workspace.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (validateGoalForm()) createMut.mutate(); }} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Title *</Label>
                <Input value={createTitle} onChange={(e) => setCreateTitle(e.target.value)} placeholder="Goal title" />
                {formErrors.title && <p className="text-xs text-destructive">{formErrors.title}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} placeholder="Describe the goal..." rows={3} />
              </div>
              <div className="space-y-1.5">
                <Label>Group *</Label>
                <Select value={createGroupId} onValueChange={(v) => { setCreateGroupId(v); setCreateOwnerId(user?.id ?? ''); setCreateDepIds([]); setCreateEvalIds([]); }}>
                  <SelectTrigger><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>{(groupsData ?? []).map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
                {formErrors.group && <p className="text-xs text-destructive">{formErrors.group}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Goal Owner *</Label>
                {!createGroupId ? (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed p-2">Please select a group first</p>
                ) : (
                  <Select value={createOwnerId} onValueChange={setCreateOwnerId}>
                    <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>{(groupMembers ?? []).map((m) => <SelectItem key={m.user.id} value={m.user.id}>{m.user.full_name} {m.user.id === user?.id ? '(you)' : ''}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {formErrors.owner && <p className="text-xs text-destructive">{formErrors.owner}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={createPriority} onValueChange={setCreatePriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{GOAL_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Due Date *</Label>
                  <Input type="date" value={createDueDate} onChange={(e) => setCreateDueDate(e.target.value)} />
                  {formErrors.due_date && <p className="text-xs text-destructive">{formErrors.due_date}</p>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Tags</Label>
                {createTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {createTags.map((tag, i) => (
                      <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                        {tag}
                        <button type="button" className="ml-0.5 rounded-full hover:bg-muted-foreground/20" onClick={() => setCreateTags((t) => t.filter((_, idx) => idx !== i))}><span className="text-[10px]">&times;</span></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={createTagInput} onChange={(e) => setCreateTagInput(e.target.value)} placeholder="Type a tag..." onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ',') && createTagInput.trim()) { e.preventDefault(); if (!createTags.includes(createTagInput.trim())) setCreateTags((t) => [...t, createTagInput.trim()]); setCreateTagInput(''); } }} />
                  <Button type="button" variant="outline" size="sm" onClick={() => { if (createTagInput.trim() && !createTags.includes(createTagInput.trim())) { setCreateTags((t) => [...t, createTagInput.trim()]); setCreateTagInput(''); } }}>Add</Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Dependencies</Label>
                {!createGroupId ? (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed p-2">Select a group first</p>
                ) : (
                  <div className="space-y-1.5">
                    {(groupGoals ?? []).filter((g) => !createDepIds.includes(g.id)).length > 0 && (
                      <Select onValueChange={(v) => { if (v && !createDepIds.includes(v)) setCreateDepIds((d) => [...d, v]); }}>
                        <SelectTrigger><SelectValue placeholder="Add dependency..." /></SelectTrigger>
                        <SelectContent>{(groupGoals ?? []).filter((g) => !createDepIds.includes(g.id)).map((g) => <SelectItem key={g.id} value={g.id}>{g.reference_id} — {g.title}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {createDepIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {createDepIds.map((depId) => { const g = (groupGoals ?? []).find((x) => x.id === depId); return (
                          <span key={depId} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">{g?.reference_id ?? depId.slice(0, 8)}<button type="button" className="ml-0.5 rounded-full hover:bg-muted-foreground/20" onClick={() => setCreateDepIds((d) => d.filter((x) => x !== depId))}><span className="text-[10px]">&times;</span></button></span>
                        ); })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1.5 pb-2">
                <Label>Evaluation Team</Label>
                {!createGroupId ? (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed p-2">Select a group first</p>
                ) : (
                  <div className="space-y-1.5">
                    {(groupMembers ?? []).filter((m) => !createEvalIds.includes(m.user.id)).length > 0 && (
                      <Select onValueChange={(v) => { if (v && !createEvalIds.includes(v)) setCreateEvalIds((e2) => [...e2, v]); }}>
                        <SelectTrigger><SelectValue placeholder="Add evaluator..." /></SelectTrigger>
                        <SelectContent>{(groupMembers ?? []).filter((m) => !createEvalIds.includes(m.user.id)).map((m) => <SelectItem key={m.user.id} value={m.user.id}>{m.user.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {createEvalIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {createEvalIds.map((uid) => { const m = (groupMembers ?? []).find((x) => x.user.id === uid); return (
                          <span key={uid} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">{m?.user.full_name ?? uid.slice(0, 8)}<button type="button" className="ml-0.5 rounded-full hover:bg-muted-foreground/20" onClick={() => setCreateEvalIds((e2) => e2.filter((x) => x !== uid))}><span className="text-[10px]">&times;</span></button></span>
                        ); })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2 shrink-0">
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); resetCreateForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark">Create Goal</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// Goal Card Component (for grid view) — FIX 4
// ===========================================================================
function GoalCard({ goal, fromTab }: { goal: Goal; fromTab?: string }) {
  const navigate = useNavigate();
  const now = new Date();
  const dueDate = goal.due_date ? new Date(goal.due_date) : null;
  const isOverdue = dueDate && dueDate < now && goal.status === 'active';
  const isCompleted = goal.status === 'completed' || goal.status === 'completed_late';
  const isActive = goal.status === 'active';
  const od = isOverdue ? Math.ceil((now.getTime() - dueDate.getTime()) / 86400000) : 0;
  const progress = goal.progress_percentage ?? 0;

  const leftBorder = isOverdue ? 'border-l-red-500' : isCompleted ? 'border-l-green-500' : isActive ? 'border-l-blue-500' : 'border-l-gray-300';

  return (
    <div
      className={cn('bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer border-l-4 flex flex-col', leftBorder)}
      onClick={() => navigate(`/goals/${goal.id}`, { state: { fromTab } })}
    >
      <div className="p-4 flex flex-col gap-2 flex-1">
        {/* Row 1: ID + badges */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{goal.reference_id}</span>
          <div className="flex items-center gap-1.5">
            <PriorityPill priority={goal.priority} />
            <StatusPill status={goal.status} />
          </div>
        </div>

        {/* Row 2: Title */}
        <Link to={`/goals/${goal.id}`} className="text-base font-semibold text-gray-900 hover:text-purple-600 line-clamp-1" onClick={(e) => e.stopPropagation()}>
          {goal.title}
        </Link>

        {/* Row 3: Group */}
        {goal.group && (
          <Link to={`/groups/${goal.group.id}`} className="flex items-center gap-1 text-sm text-gray-500 hover:text-purple-600 w-fit" onClick={(e) => e.stopPropagation()}>
            <Users className="h-3.5 w-3.5" />
            {goal.group.name}
          </Link>
        )}

        {/* Row 4: Description */}
        {goal.description ? (
          <p className="text-sm text-gray-500 line-clamp-2">{goal.description}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">No description</p>
        )}

        {/* Divider */}
        <div className="border-t border-gray-100 my-1" />

        {/* Row 5: Due date */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Calendar className="h-3.5 w-3.5" />
          <span>{dueDate ? dueDate.toLocaleDateString() : 'No due date'}</span>
          {od > 0 && <span className="rounded-full bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold">{od}d</span>}
        </div>

        {/* Row 7: Owner + progress */}
        <div className="flex items-center justify-between mt-auto pt-1">
          <div className="flex items-center gap-1.5 min-w-0 max-w-[60%]">
            {goal.owner && <UserAvatar name={goal.owner.full_name} avatarUrl={goal.owner.avatar_url} size="xs" />}
            <span className="text-xs text-gray-500 truncate">{goal.owner?.full_name ?? '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div className={cn('h-full rounded-full', progressColor(progress))} style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs font-semibold text-gray-700">{progress}%</span>
          </div>
        </div>
      </div>

      {/* Full-width bottom progress bar */}
      <div className="h-1.5 rounded-b-xl bg-gray-100 overflow-hidden">
        <div className={cn('h-full transition-all', progressColor(progress))} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

// ===========================================================================
// TAB 2 — Goals Dashboard
// ===========================================================================
const STATUS_CHART_COLORS: Record<string, string> = {
  draft: '#6B7280', active: '#3B82F6', completed: '#22C55E',
  completed_late: '#EF4444', cancelled: '#9CA3AF',
};
const PRIORITY_CHART_COLORS: Record<string, string> = {
  low: '#3B82F6', medium: '#F59E0B', high: '#EF4444', critical: '#7C2D12',
};

const DASH_TABS = ['Overview', 'Progress', 'Quality', 'Trends', 'Owners'] as const;

function GoalsDashboardTab() {
  const [dashTab, setDashTab] = useState<string>('Overview');

  const { data, isLoading } = useQuery({
    queryKey: ['work-goals-all'],
    queryFn: () => goalService.list({ limit: 100 }).then((r) => r.data.data),
  });

  const goals: Goal[] = data ?? [];

  if (isLoading) {
    return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  }

  const total = goals.length;
  const completed = goals.filter((g) => g.status === 'completed' || g.status === 'completed_late').length;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const onTime = goals.filter((g) => g.status === 'completed').length;
  const onTimeRate = completed > 0 ? Math.round((onTime / completed) * 100) : 0;
  const avgProgress = total > 0 ? Math.round(goals.reduce((s, g) => s + (g.progress_percentage ?? 0), 0) / total) : 0;
  const atRisk = goals.filter((g) => g.status === 'active' && g.due_date && new Date(g.due_date) < new Date()).length;
  const drafts = goals.filter((g) => g.status === 'draft').length;
  const active = goals.filter((g) => g.status === 'active').length;
  const scoredGoals = goals.filter((g) => g.evaluation_score != null);
  const avgScore = scoredGoals.length > 0
    ? scoredGoals.reduce((s, g) => s + g.evaluation_score!, 0) / scoredGoals.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Stat cards 2x4 — FIX 1 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <GoalStatCard icon={Target} iconColor="text-purple-600" iconBg="bg-purple-600" label="Total Goals" value={total} sub={`${active} active`} />
        <GoalStatCard icon={CheckCircle} iconColor="text-green-600" iconBg="bg-green-600" label="Completed" value={completed} sub={`${completionRate}% rate`} barValue={completionRate} barColor="bg-green-500" />
        <GoalStatCard icon={Clock} iconColor="text-blue-600" iconBg="bg-blue-600" label="On-Time Delivery" value={`${onTimeRate}%`} sub={`${onTime} on time`} barValue={onTimeRate} barColor="bg-blue-500" />
        <GoalStatCard icon={Star} iconColor="text-yellow-500" iconBg="bg-yellow-500" label="Average Score" value={avgScore > 0 ? avgScore.toFixed(1) : '—'} barValue={avgScore > 0 ? Math.round((avgScore / 5) * 100) : undefined} barColor="bg-yellow-500" />
        <GoalStatCard icon={AlertTriangle} iconColor="text-red-600" iconBg="bg-red-600" label="At Risk" value={atRisk} sub="past due" valueColor={atRisk > 0 ? 'text-red-600' : undefined} />
        <GoalStatCard icon={TrendingUp} iconColor="text-indigo-600" iconBg="bg-indigo-600" label="Avg Progress" value={`${avgProgress}%`} barValue={avgProgress} barColor="bg-indigo-500" />
        <GoalStatCard icon={FileText} iconColor="text-gray-500" iconBg="bg-gray-500" label="Draft Goals" value={drafts} sub="not started" />
        <GoalStatCard icon={ClipboardList} iconColor="text-orange-500" iconBg="bg-orange-500" label="Pending Evaluations" value={0} sub="awaiting review" />
      </div>

      {/* Sub-tab bar */}
      <div className="flex gap-1 border-b">
        {DASH_TABS.map((tab) => (
          <button key={tab} onClick={() => setDashTab(tab)} className={cn('px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px', dashTab === tab ? 'border-brand-primary text-brand-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {tab}
          </button>
        ))}
      </div>

      {dashTab === 'Overview' && <DashOverviewTab goals={goals} />}
      {dashTab === 'Progress' && <DashProgressTab goals={goals} />}
      {dashTab === 'Quality' && <DashQualityTab goals={goals} />}
      {dashTab === 'Trends' && <DashTrendsTab goals={goals} />}
      {dashTab === 'Owners' && <DashOwnersTab goals={goals} />}
    </div>
  );
}

// --- Goal Stat Card (FIX 1) ---
function GoalStatCard({ icon: Icon, iconColor, iconBg, label, value, sub, valueColor, barValue, barColor }: {
  icon: React.ElementType; iconColor: string; iconBg: string;
  label: string; value: string | number; sub?: string; valueColor?: string;
  barValue?: number; barColor?: string;
}) {
  return (
    <div className="relative rounded-xl border border-gray-100 bg-white shadow-sm p-5 flex flex-col">
      {/* Icon badge — top right */}
      <div className="absolute top-3 right-3 flex items-center justify-center" style={{ width: 36, height: 36 }}>
        <div className={cn('absolute inset-0 rounded-full opacity-15', iconBg)} />
        <Icon className={cn('h-4.5 w-4.5 relative z-10', iconColor)} style={{ width: 18, height: 18 }} />
      </div>
      <p className="text-sm font-medium text-gray-500 mt-1">{label}</p>
      <p className={cn('text-3xl font-bold text-gray-900 mt-0.5', valueColor)}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      {/* Bottom progress bar */}
      {barValue != null && (
        <div className="mt-auto pt-3">
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <div className={cn('h-full rounded-full transition-all', barColor ?? 'bg-brand-primary')} style={{ width: `${Math.min(barValue, 100)}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Sub-tab: Overview
// ---------------------------------------------------------------------------
function DashOverviewTab({ goals }: { goals: Goal[] }) {
  const now = new Date();

  const statusCounts: Record<string, number> = {};
  const priorityCounts: Record<string, number> = {};
  const groupCounts: Record<string, number> = {};
  for (const g of goals) {
    statusCounts[g.status] = (statusCounts[g.status] ?? 0) + 1;
    priorityCounts[g.priority] = (priorityCounts[g.priority] ?? 0) + 1;
    const gn = g.group?.name ?? 'No Group';
    groupCounts[gn] = (groupCounts[gn] ?? 0) + 1;
  }

  const statusData = Object.entries(statusCounts).map(([name, value]) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value,
    fill: STATUS_CHART_COLORS[name] ?? '#94A3B8',
  }));
  const priorityData = Object.entries(priorityCounts).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), value,
    fill: PRIORITY_CHART_COLORS[name] ?? '#94A3B8',
  }));
  const groupData = Object.entries(groupCounts).map(([name, value]) => ({ name, value }));

  const oneWeek = new Date(now.getTime() + 7 * 86400000);
  const twoWeeks = new Date(now.getTime() + 14 * 86400000);
  const dueDateCats = { Overdue: 0, 'This Week': 0, 'Next 2 Weeks': 0, Future: 0 };
  for (const g of goals) {
    if (!g.due_date || g.status === 'completed' || g.status === 'completed_late' || g.status === 'cancelled') continue;
    const d = new Date(g.due_date);
    if (d < now) dueDateCats['Overdue']++;
    else if (d <= oneWeek) dueDateCats['This Week']++;
    else if (d <= twoWeeks) dueDateCats['Next 2 Weeks']++;
    else dueDateCats['Future']++;
  }
  const dueDateColors: Record<string, string> = { Overdue: '#EF4444', 'This Week': '#F97316', 'Next 2 Weeks': '#EAB308', Future: '#22C55E' };
  const dueDateData = Object.entries(dueDateCats).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, fill: dueDateColors[name] }));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Status</p>
        {statusData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">{statusData.map((e) => <Cell key={e.name} fill={e.fill} />)}</Pie><RTooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} /></PieChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Priority</p>
        {priorityData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={priorityData} layout="vertical" margin={{ left: 8 }}><XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="name" fontSize={11} width={55} /><RTooltip /><Bar dataKey="value" radius={[0, 4, 4, 0]}>{priorityData.map((e) => <Cell key={e.name} fill={e.fill} />)}</Bar></BarChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Group</p>
        {groupData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={groupData} layout="vertical" margin={{ left: 8 }}><XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="name" fontSize={11} width={80} /><RTooltip /><Bar dataKey="value" fill="#7C3AED" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Due Date</p>
        {dueDateData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={dueDateData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">{dueDateData.map((e) => <Cell key={e.name} fill={e.fill} />)}</Pie><RTooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} /></PieChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Sub-tab: Progress
// ---------------------------------------------------------------------------
function DashProgressTab({ goals }: { goals: Goal[] }) {
  const total = goals.length;
  const avgProgress = total > 0 ? Math.round(goals.reduce((s, g) => s + (g.progress_percentage ?? 0), 0) / total) : 0;
  const buckets = [
    { label: '0–25%', min: 0, max: 25, color: '#EF4444' },
    { label: '26–50%', min: 26, max: 50, color: '#F97316' },
    { label: '51–75%', min: 51, max: 75, color: '#EAB308' },
    { label: '76–99%', min: 76, max: 99, color: '#3B82F6' },
    { label: '100%', min: 100, max: 100, color: '#22C55E' },
  ];
  const bucketCounts = buckets.map((b) => ({ ...b, count: goals.filter((g) => { const p = g.progress_percentage ?? 0; return p >= b.min && p <= b.max; }).length }));
  const donutData = bucketCounts.filter((b) => b.count > 0).map((b) => ({ name: b.label, value: b.count, fill: b.color }));
  const withTasks = goals.filter((g) => g.total_tasks > 0).length;
  const withoutTasks = goals.filter((g) => g.total_tasks === 0).length;
  const avgTasksPerGoal = total > 0 ? (goals.reduce((s, g) => s + g.total_tasks, 0) / total).toFixed(1) : '0.0';

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Progress Distribution</p>
        <div className="flex flex-col items-center">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={donutData.length > 0 ? donutData : [{ name: 'None', value: 1, fill: '#E5E7EB' }]} cx="50%" cy="50%" innerRadius={50} outerRadius={72} paddingAngle={1} dataKey="value">{(donutData.length > 0 ? donutData : [{ name: 'None', value: 1, fill: '#E5E7EB' }]).map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><RTooltip /></PieChart>
          </ResponsiveContainer>
          <p className="text-2xl font-bold -mt-2">{avgProgress}%</p>
          <p className="text-xs text-muted-foreground mb-3">Average Progress</p>
          <div className="w-full space-y-1.5">
            {bucketCounts.map((b) => { const pct = total > 0 ? Math.round((b.count / total) * 100) : 0; return (
              <div key={b.label} className="flex items-center gap-2 text-xs">
                <span className="w-14 text-muted-foreground">{b.label}</span>
                <span className="w-6 text-right font-medium">{b.count}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: b.color }} /></div>
                <span className="w-8 text-right text-muted-foreground">{pct}%</span>
              </div>
            ); })}
          </div>
        </div>
      </Card>
      <Card className="p-4 space-y-5">
        <div>
          <p className="text-sm font-semibold mb-3">Task & Dependency Analysis</p>
          <p className="text-xs font-medium text-muted-foreground mb-2">Task Linkage</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3 text-center"><p className="text-xl font-bold text-green-600">{withTasks}</p><p className="text-[11px] text-muted-foreground">With Tasks</p></div>
            <div className="rounded-lg border p-3 text-center"><p className="text-xl font-bold text-muted-foreground">{withoutTasks}</p><p className="text-[11px] text-muted-foreground">Without Tasks</p></div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Average tasks per goal: {avgTasksPerGoal}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Dependencies</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3 text-center"><p className="text-xl font-bold">—</p><p className="text-[11px] text-muted-foreground">Has Dependencies</p></div>
            <div className="rounded-lg border p-3 text-center"><p className="text-xl font-bold">—</p><p className="text-[11px] text-muted-foreground">Blocked</p></div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Sub-tab: Quality
// ---------------------------------------------------------------------------
function DashQualityTab({ goals }: { goals: Goal[] }) {
  const completedGoals = goals.filter((g) => g.status === 'completed' || g.status === 'completed_late');
  const evaluated = completedGoals.filter((g) => g.evaluation_score != null);
  const pending = completedGoals.filter((g) => g.evaluation_score == null);
  const evalPct = completedGoals.length > 0 ? Math.round((evaluated.length / completedGoals.length) * 100) : 0;
  const scoreBuckets = [1, 2, 3, 4, 5];
  const scoreData = scoreBuckets.map((s) => ({ name: `${s} Star${s > 1 ? 's' : ''}`, value: evaluated.filter((g) => Math.round(g.evaluation_score!) === s).length }));
  const evalDonut = [
    { name: 'Evaluated', value: evaluated.length, fill: '#22C55E' },
    { name: 'Pending', value: Math.max(pending.length, completedGoals.length === 0 ? 1 : 0), fill: '#EAB308' },
  ].filter((d) => d.value > 0);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Evaluation Coverage</p>
        <div className="flex flex-col items-center">
          <ResponsiveContainer width="100%" height={160}>
            <PieChart><Pie data={evalDonut} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={2}>{evalDonut.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><RTooltip /></PieChart>
          </ResponsiveContainer>
          <p className="text-xl font-bold -mt-1">{evalPct}%</p>
          <p className="text-xs text-muted-foreground mb-3">Evaluated</p>
          <div className="w-full space-y-1 text-xs">
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-green-500" /><span>Evaluated</span><span className="ml-auto font-medium">{evaluated.length}</span></div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /><span>Pending</span><span className="ml-auto font-medium">{pending.length}</span></div>
          </div>
          {pending.length > 0 && (
            <div className="mt-3 w-full rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-700">
              <AlertTriangle className="inline h-3 w-3 mr-1" />{pending.length} completed goal{pending.length > 1 ? 's' : ''} need{pending.length === 1 ? 's' : ''} evaluation
            </div>
          )}
        </div>
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Score Distribution</p>
        {evaluated.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scoreData} margin={{ left: -10 }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><RTooltip /><Bar dataKey="value" fill="#7C3AED" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground"><Star className="h-8 w-8 mb-2" /><p className="text-sm">No evaluations yet</p></div>
        )}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Top Evaluators</p>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground"><User2 className="h-8 w-8 mb-2" /><p className="text-sm">No evaluators yet</p><p className="text-xs mt-1">Evaluator data will appear here</p></div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Sub-tab: Trends
// ---------------------------------------------------------------------------
function DashTrendsTab({ goals }: { goals: Goal[] }) {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000);
  const creationSeries: Record<string, number> = {};
  const completionSeries: Record<string, number> = {};
  for (let d = new Date(thirtyDaysAgo); d <= now; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    creationSeries[key] = 0; completionSeries[key] = 0;
  }
  for (const g of goals) {
    const cKey = g.created_at?.slice(0, 10);
    if (cKey && creationSeries[cKey] !== undefined) creationSeries[cKey]++;
    if (g.completed_at) { const dKey = g.completed_at.slice(0, 10); if (dKey && completionSeries[dKey] !== undefined) completionSeries[dKey]++; }
  }
  const trendData = Object.keys(creationSeries).sort().map((date) => ({ date: new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), Created: creationSeries[date], Completed: completionSeries[date] }));
  const totalCreated = Object.values(creationSeries).reduce((a, b) => a + b, 0);
  const totalCompleted = Object.values(completionSeries).reduce((a, b) => a + b, 0);
  const days = trendData.length || 1;

  const weeklyBuckets: { date: string; avgProgress: number; avgScore: number }[] = [];
  for (let i = 0; i < 4; i++) {
    const weekEnd = new Date(now.getTime() - i * 7 * 86400000);
    const weekStart = new Date(weekEnd.getTime() - 7 * 86400000);
    const weekGoals = goals.filter((g) => new Date(g.created_at) <= weekEnd);
    const wp = weekGoals.length > 0 ? Math.round(weekGoals.reduce((s, g) => s + (g.progress_percentage ?? 0), 0) / weekGoals.length) : 0;
    const scored = weekGoals.filter((g) => g.evaluation_score != null);
    const ws = scored.length > 0 ? +(scored.reduce((s, g) => s + g.evaluation_score!, 0) / scored.length).toFixed(1) : 0;
    weeklyBuckets.unshift({ date: weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), avgProgress: wp, avgScore: ws });
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="p-4">
        <p className="mb-1 text-sm font-semibold">Goal Creation vs Completion</p>
        <p className="mb-3 text-[11px] text-muted-foreground">Avg created: {(totalCreated / days).toFixed(1)}/day &middot; Avg completed: {(totalCompleted / days).toFixed(1)}/day</p>
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData} margin={{ left: -10 }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="date" fontSize={10} interval={Math.max(0, Math.floor(trendData.length / 6))} /><YAxis fontSize={11} allowDecimals={false} /><RTooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} /><Line type="monotone" dataKey="Created" stroke="#374151" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="Completed" stroke="#22C55E" strokeWidth={2} dot={false} /></LineChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Performance Trends</p>
        {weeklyBuckets.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weeklyBuckets} margin={{ left: -10, right: 10 }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="date" fontSize={10} /><YAxis yAxisId="left" fontSize={11} domain={[0, 100]} unit="%" /><YAxis yAxisId="right" orientation="right" fontSize={11} domain={[0, 5]} /><RTooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} /><Line yAxisId="left" type="monotone" dataKey="avgProgress" name="Avg Progress %" stroke="#3B82F6" strokeWidth={2} dot /><Line yAxisId="right" type="monotone" dataKey="avgScore" name="Avg Score" stroke="#F59E0B" strokeWidth={2} dot /></LineChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Sub-tab: Owners
// ---------------------------------------------------------------------------
function DashOwnersTab({ goals }: { goals: Goal[] }) {
  const [ownerSearch, setOwnerSearch] = useState('');
  const now = new Date();

  const ownerMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; avatarUrl: string | null; total: number; completed: number; overdue: number; progressSum: number; scoreSum: number; scoreCount: number }>();
    for (const g of goals) {
      if (!g.owner) continue;
      const key = g.owner.id;
      if (!map.has(key)) map.set(key, { id: key, name: g.owner.full_name, avatarUrl: g.owner.avatar_url, total: 0, completed: 0, overdue: 0, progressSum: 0, scoreSum: 0, scoreCount: 0 });
      const o = map.get(key)!;
      o.total++;
      if (g.status === 'completed' || g.status === 'completed_late') o.completed++;
      if (g.status === 'active' && g.due_date && new Date(g.due_date) < now) o.overdue++;
      o.progressSum += g.progress_percentage ?? 0;
      if (g.evaluation_score != null) { o.scoreSum += g.evaluation_score; o.scoreCount++; }
    }
    return Array.from(map.values());
  }, [goals, now]);

  const topPerformers = [...ownerMap].filter((o) => o.total > 0).sort((a, b) => (b.completed / b.total) - (a.completed / a.total)).slice(0, 5);
  const needsAttention = [...ownerMap].filter((o) => o.overdue > 0).sort((a, b) => b.overdue - a.overdue);
  const filtered = ownerSearch ? ownerMap.filter((o) => o.name.toLowerCase().includes(ownerSearch.toLowerCase())) : ownerMap;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3"><Trophy className="h-4 w-4 text-amber-500" /><p className="text-sm font-semibold">Top Performers</p></div>
          {topPerformers.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No data yet</p> : (
            <div className="space-y-2">{topPerformers.map((o, i) => { const pct = o.total > 0 ? Math.round((o.completed / o.total) * 100) : 0; return (
              <div key={o.id} className="flex items-center gap-2.5"><span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span><UserAvatar name={o.name} avatarUrl={o.avatarUrl} size="xs" /><span className="text-sm flex-1 truncate">{o.name}</span><span className="text-xs text-muted-foreground">{o.completed}/{o.total} completed</span><span className={cn('text-xs font-semibold', pct > 50 ? 'text-green-600' : 'text-muted-foreground')}>{pct}%</span></div>
            ); })}</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3"><AlertCircle className="h-4 w-4 text-amber-500" /><p className="text-sm font-semibold">Needs Attention</p></div>
          {needsAttention.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No overdue goals</p> : (
            <div className="space-y-2">{needsAttention.map((o) => { const pct = o.total > 0 ? Math.round(o.progressSum / o.total) : 0; return (
              <div key={o.id} className="flex items-center gap-2.5"><UserAvatar name={o.name} avatarUrl={o.avatarUrl} size="xs" /><span className="text-sm flex-1 truncate">{o.name}</span><span className="text-xs text-muted-foreground">{o.total} owned, {o.overdue} overdue</span><span className="rounded-full bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold">{o.overdue}</span><span className="text-xs text-muted-foreground">{pct}%</span></div>
            ); })}</div>
          )}
        </Card>
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <p className="text-sm font-semibold">All Goal Owners</p>
          <div className="relative max-w-xs flex-1"><Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search owners..." value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} className="pl-8 h-8 text-sm" /></div>
        </div>
        {filtered.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No owners found</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-4 font-medium">Owner</th><th className="py-2 pr-4 font-medium">Goals</th><th className="py-2 pr-4 font-medium">Completion</th><th className="py-2 pr-4 font-medium">Progress</th><th className="py-2 pr-4 font-medium">Avg Score</th><th className="py-2 font-medium">Overdue</th></tr></thead>
              <tbody>{filtered.map((o) => {
                const completionPct = o.total > 0 ? Math.round((o.completed / o.total) * 100) : 0;
                const avgProg = o.total > 0 ? Math.round(o.progressSum / o.total) : 0;
                const avgSc = o.scoreCount > 0 ? (o.scoreSum / o.scoreCount).toFixed(1) : '—';
                return (
                  <tr key={o.id} className="border-b last:border-0">
                    <td className="py-2 pr-4"><div className="flex items-center gap-2"><UserAvatar name={o.name} avatarUrl={o.avatarUrl} size="xs" /><span className="truncate">{o.name}</span></div></td>
                    <td className="py-2 pr-4 text-xs">{o.total} <span className="text-muted-foreground">({o.completed} done)</span></td>
                    <td className="py-2 pr-4"><div className="flex items-center gap-2"><div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-green-500" style={{ width: `${completionPct}%` }} /></div><span className="text-xs">{completionPct}%</span></div></td>
                    <td className="py-2 pr-4"><div className="flex items-center gap-2"><div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${avgProg}%` }} /></div><span className="text-xs">{avgProg}%</span></div></td>
                    <td className="py-2 pr-4 text-xs">{avgSc}</td>
                    <td className="py-2">{o.overdue > 0 ? <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-semibold">{o.overdue}</span> : <span className="text-xs text-muted-foreground">0</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ===========================================================================
// TAB 3 — Sprints List
// ===========================================================================
function SprintsListTab() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const fromTab = sp.get('tab') || 'sprints-list';
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const hasFilters = !!statusFilter || !!groupFilter;
  const activeFilterCount = [statusFilter, groupFilter].filter(Boolean).length;

  const params = {
    limit: 50, sort: 'created_at' as const, order: 'desc' as const,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(groupFilter && { group_id: groupFilter }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['work-sprints', params],
    queryFn: () => sprintService.list(params).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups-for-sprint-filter'],
    queryFn: () => groupService.list({ limit: 100 }).then((r) => r.data.data),
  });

  const sprints = data?.data ?? [];
  const now = new Date();

  function clearFilters() { setStatusFilter(''); setGroupFilter(''); }

  return (
    <div className="space-y-4">
      {/* Toolbar: search + filters toggle + create */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search sprints..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1.5"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </Button>
        <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New Sprint
        </Button>
      </div>

      {/* Filter dropdowns — hidden by default */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border">
            <option value="">All Statuses</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border">
            <option value="">All Groups</option>
            {(groupsData ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {hasFilters && (
            <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
              <X className="h-3 w-3" /> Clear filters
            </button>
          )}
        </div>
      )}

      {/* Sprint cards */}
      {isLoading ? (
        <div className="space-y-4">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : sprints.length === 0 ? (
        <EmptyState icon={Zap} title="No sprints found" description="Create your first sprint to start planning." />
      ) : (
        <div className="space-y-3">
          {sprints.map((sprint) => {
            const isLate = sprint.status === 'active' && new Date(sprint.end_date) < now;
            const isLive = sprint.status === 'active' && new Date(sprint.start_date) <= now && new Date(sprint.end_date) >= now;
            const displayStatus = isLate ? 'late' : isLive ? 'live' : sprint.status;
            const StatusIcon = isLate ? AlertTriangle : isLive ? Play : sprint.status === 'completed' ? CheckCircle : sprint.status === 'planning' ? Construction : Clock;
            const statusIconColor = isLate ? 'text-amber-500' : isLive ? 'text-emerald-500' : sprint.status === 'completed' ? 'text-emerald-500' : 'text-muted-foreground';
            return (
              <Card key={sprint.id} className="p-4 cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate(`/sprints/${sprint.id}`, { state: { fromTab } })}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {StatusIcon && <StatusIcon className={cn('h-4 w-4 shrink-0', statusIconColor)} />}
                      <span className="font-mono text-[11px] text-muted-foreground">{sprint.reference_id}</span>
                      <StatusBadge status={displayStatus} />
                    </div>
                    <p className="text-sm font-semibold">{sprint.name}</p>
                    {sprint.description && <p className="text-xs text-muted-foreground line-clamp-2">{sprint.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                      {sprint.group && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">{sprint.group.name}</span>}
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(sprint.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} &rarr; {new Date(sprint.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      {sprint.owner && <div className="flex items-center gap-1"><UserAvatar name={sprint.owner.full_name} avatarUrl={sprint.owner.avatar_url} size="xs" /><span>{sprint.owner.full_name}</span></div>}
                    </div>
                    {sprint.status !== 'planning' && (
                      <ProgressBar value={sprint.status === 'completed' ? 100 : Math.max(0, Math.min(100, Math.round(((now.getTime() - new Date(sprint.start_date).getTime()) / (new Date(sprint.end_date).getTime() - new Date(sprint.start_date).getTime())) * 100)))} size="sm" />
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end"><DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/sprints/${sprint.id}`, { state: { fromTab } }); }}>View Details</DropdownMenuItem></DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Use the full CreateSprintDialog with stage leader selection */}
      <CreateSprintDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['work-sprints'] })}
      />
    </div>
  );
}

// ===========================================================================
// TAB 4 — Sprints Dashboard (full analytics with sub-tabs)
// ===========================================================================
const SPRINT_DASH_TABS = ['Overview', 'Velocity', 'Quality', 'Trends', 'Owners'] as const;

function SprintsDashboardTab() {
  const [dashTab, setDashTab] = useState<string>('Overview');
  const { data, isLoading } = useQuery({ queryKey: ['work-sprints-all'], queryFn: () => sprintService.list({ limit: 100 }).then((r) => r.data.data) });
  const sprints: Sprint[] = data ?? [];

  if (isLoading) return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>;

  if (sprints.length === 0) {
    return (
      <EmptyState
        icon={Zap}
        title="No sprints in this workspace"
        description="Create a sprint from the Sprints List tab, or switch to a workspace that has sprints."
      />
    );
  }

  const now = new Date();
  const total = sprints.length;
  const completed = sprints.filter((s) => s.status === 'completed').length;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const lateCount = sprints.filter((s) => s.status === 'active' && new Date(s.end_date) < now).length;
  const activeCount = sprints.filter((s) => s.status === 'active').length;
  const onTimeCompletedPct = completed > 0 ? Math.round((sprints.filter((s) => s.status === 'completed' && (!s.completed_at || new Date(s.completed_at) <= new Date(s.end_date))).length / completed) * 100) : 0;
  const avgDuration = completed > 0 ? Math.round(sprints.filter((s) => s.status === 'completed').reduce((sum, s) => sum + (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000, 0) / completed) : 0;
  const scoredSprints = sprints.filter((s) => s.evaluation_score != null);
  const avgScore = scoredSprints.length > 0 ? scoredSprints.reduce((s2, s) => s2 + s.evaluation_score!, 0) / scoredSprints.length : 0;
  const pendingEvals = sprints.filter((s) => s.status === 'completed' && s.evaluation_score == null).length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SprintStatCard icon={Layers} iconColor="text-blue-600" iconBg="bg-blue-600" label="Total Sprints" value={total} sub={`${activeCount} active`} />
        <SprintStatCard icon={CheckCircle} iconColor="text-green-600" iconBg="bg-green-600" label="Completed" value={completed} sub={`${completionRate}% completion rate`} barValue={completionRate} barColor="bg-green-500" />
        <SprintStatCard icon={Clock} iconColor="text-amber-600" iconBg="bg-amber-600" label="On-Time Delivery" value={`${onTimeCompletedPct}%`} sub="delivered by due date" barValue={onTimeCompletedPct} barColor={onTimeCompletedPct >= 80 ? 'bg-green-500' : onTimeCompletedPct >= 60 ? 'bg-yellow-500' : 'bg-red-500'} />
        <SprintStatCard icon={Star} iconColor="text-yellow-600" iconBg="bg-yellow-600" label="Average Score" value={avgScore > 0 ? avgScore.toFixed(1) : '—'} sub="evaluation rating" barValue={(avgScore / 5) * 100} barColor="bg-primary" />
        <SprintStatCard icon={AlertTriangle} iconColor="text-red-600" iconBg="bg-red-600" label="Late Sprints" value={lateCount} sub="past due date" textColor={lateCount > 0 ? 'text-red-600' : undefined} />
        <SprintStatCard icon={Calendar} iconColor="text-purple-600" iconBg="bg-purple-600" label="Avg Duration" value={`${avgDuration}d`} sub="average sprint length" />
        <SprintStatCard icon={Timer} iconColor="text-blue-600" iconBg="bg-blue-600" label="Active Sprints" value={activeCount} sub="currently running" textColor={activeCount > 0 ? 'text-blue-600' : undefined} />
        <SprintStatCard icon={ClipboardList} iconColor="text-yellow-600" iconBg="bg-yellow-600" label="Pending Evaluations" value={pendingEvals} sub="awaiting review" textColor={pendingEvals > 0 ? 'text-yellow-600' : undefined} />
      </div>

      {/* Sub-tabs */}
      <Tabs value={dashTab} onValueChange={setDashTab}>
        <TabsList>
          {SPRINT_DASH_TABS.map((t) => <TabsTrigger key={t} value={t}>{t}</TabsTrigger>)}
        </TabsList>

        {/* Overview: Distribution charts */}
        <TabsContent value="Overview" className="mt-6 space-y-6">
          <SprintOverviewCharts sprints={sprints} />
        </TabsContent>

        {/* Velocity */}
        <TabsContent value="Velocity" className="mt-6 space-y-6">
          <SprintVelocitySection sprints={sprints} />
        </TabsContent>

        {/* Quality */}
        <TabsContent value="Quality" className="mt-6 space-y-6">
          <SprintQualitySection sprints={sprints} />
        </TabsContent>

        {/* Trends */}
        <TabsContent value="Trends" className="mt-6 space-y-6">
          <SprintTrendsSection sprints={sprints} />
        </TabsContent>

        {/* Owners */}
        <TabsContent value="Owners" className="mt-6 space-y-6">
          <SprintOwnersSection sprints={sprints} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SprintStatCard({ icon: Icon, iconColor, iconBg, label, value, sub, barValue, barColor, textColor }: {
  icon: typeof Layers; iconColor: string; iconBg: string; label: string; value: string | number; sub?: string;
  barValue?: number; barColor?: string; textColor?: string;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
          <p className={cn('text-2xl font-bold', textColor)}>{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
        <div className={cn('rounded-full p-2.5 bg-opacity-10', iconBg.replace('bg-', 'bg-') + '/10')}>
          <Icon className={cn('h-5 w-5', iconColor)} />
        </div>
      </div>
      {barValue !== undefined && <ProgressBar value={barValue} size="sm" showLabel={false} className={cn('mt-2', barColor)} />}
    </Card>
  );
}

// ── Overview sub-tab ──
function SprintOverviewCharts({ sprints }: { sprints: Sprint[] }) {
  const statusCounts: Record<string, number> = {};
  const groupCounts: Record<string, { total: number; completed: number }> = {};
  for (const s of sprints) {
    statusCounts[s.status] = (statusCounts[s.status] ?? 0) + 1;
    const gn = s.group?.name ?? 'No Group';
    if (!groupCounts[gn]) groupCounts[gn] = { total: 0, completed: 0 };
    groupCounts[gn].total += 1;
    if (s.status === 'completed') groupCounts[gn].completed += 1;
  }
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value, fill: STATUS_CHART_COLORS[name] ?? '#94A3B8' }));
  const groupData = Object.entries(groupCounts).map(([name, v]) => ({ name, total: v.total, completed: v.completed }));

  // Duration distribution
  const durationBuckets = [
    { range: '< 1 week', count: 0, color: '#3B82F6' },
    { range: '1-2 weeks', count: 0, color: '#22C55E' },
    { range: '2-4 weeks', count: 0, color: '#F59E0B' },
    { range: '> 4 weeks', count: 0, color: '#EF4444' },
  ];
  for (const s of sprints) {
    const days = (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000;
    if (days < 7) durationBuckets[0].count++;
    else if (days < 14) durationBuckets[1].count++;
    else if (days < 28) durationBuckets[2].count++;
    else durationBuckets[3].count++;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Status</p>
        {statusData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} labelLine={false} dataKey="value"
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
                  if (percent < 0.05) return null;
                  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + r * Math.cos(-midAngle * Math.PI / 180);
                  const y = cy + r * Math.sin(-midAngle * Math.PI / 180);
                  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-medium">{`${(percent * 100).toFixed(0)}%`}</text>;
                }}
              >{statusData.map((e) => <Cell key={e.name} fill={e.fill} />)}</Pie>
              <RTooltip /><Legend formatter={(v: string) => <span className="text-sm">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Group</p>
        {groupData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={groupData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
              <RTooltip />
              <Bar dataKey="total" name="Total" fill="#7C3AED" radius={[0, 4, 4, 0]} />
              <Bar dataKey="completed" name="Completed" fill="#22C55E" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">By Duration</p>
        {durationBuckets.some((d) => d.count > 0) ? (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={durationBuckets.filter((d) => d.count > 0)} dataKey="count" nameKey="range" cx="50%" cy="50%" outerRadius={80} labelLine={false}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
                  if (percent < 0.05) return null;
                  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + r * Math.cos(-midAngle * Math.PI / 180);
                  const y = cy + r * Math.sin(-midAngle * Math.PI / 180);
                  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-medium">{`${(percent * 100).toFixed(0)}%`}</text>;
                }}
              >{durationBuckets.map((e) => <Cell key={e.range} fill={e.color} />)}</Pie>
              <RTooltip /><Legend formatter={(v: string) => <span className="text-sm">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">No data</p>}
      </Card>
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">Goal Coverage</p>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-muted p-4 text-center">
              <p className="text-2xl font-bold text-primary">{sprints.filter((s) => (s as any).sprint_goals_count > 0 || s.status !== 'planning').length}</p>
              <p className="text-sm text-muted-foreground">With Goals</p>
            </div>
            <div className="rounded-lg bg-muted p-4 text-center">
              <p className="text-2xl font-bold text-muted-foreground">{sprints.filter((s) => !((s as any).sprint_goals_count > 0) && s.status === 'planning').length}</p>
              <p className="text-sm text-muted-foreground">Without Goals</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Velocity sub-tab ──
function SprintVelocitySection({ sprints }: { sprints: Sprint[] }) {
  const completedSprints = sprints.filter((s) => s.status === 'completed');
  const avgCompletionRate = completedSprints.length > 0 ? Math.round(completedSprints.reduce((sum, _s) => sum + 100, 0) / completedSprints.length) : 0;
  // We don't have per-sprint task counts in the list response, show what we can
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5" /><p className="text-sm font-semibold">Velocity Summary</p></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg bg-muted p-4 text-center">
            <p className="text-3xl font-bold text-primary">{sprints.length}</p>
            <p className="text-sm text-muted-foreground">Total Sprints</p>
          </div>
          <div className="rounded-lg bg-muted p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{completedSprints.length > 0 ? `${avgCompletionRate}%` : '—'}</p>
            <p className="text-sm text-muted-foreground">Avg Completion Rate</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-sm"><span>Sprint Completion Rate</span><span className="font-medium">{sprints.length > 0 ? Math.round((completedSprints.length / sprints.length) * 100) : 0}%</span></div>
          <ProgressBar value={sprints.length > 0 ? Math.round((completedSprints.length / sprints.length) * 100) : 0} size="sm" showLabel={false} className="mt-1" />
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5" /><p className="text-sm font-semibold">Duration Analysis</p></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Shortest Sprint</p>
            <p className="text-2xl font-bold">{completedSprints.length > 0 ? `${Math.round(Math.min(...completedSprints.map((s) => (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000)))}d` : '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Longest Sprint</p>
            <p className="text-2xl font-bold">{completedSprints.length > 0 ? `${Math.round(Math.max(...completedSprints.map((s) => (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000)))}d` : '—'}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm text-muted-foreground">Average Duration</span>
          <span className="font-medium">{completedSprints.length > 0 ? `${Math.round(completedSprints.reduce((sum, s) => sum + (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000, 0) / completedSprints.length)}d` : '—'}</span>
        </div>
      </Card>
    </div>
  );
}

// ── Quality sub-tab ──
function SprintQualitySection({ sprints }: { sprints: Sprint[] }) {
  const completedSprints = sprints.filter((s) => s.status === 'completed');
  const scoredSprints = sprints.filter((s) => s.evaluation_score != null);
  const evaluatedCount = scoredSprints.length;
  const pendingCount = completedSprints.filter((s) => s.evaluation_score == null).length;
  const evaluationRate = completedSprints.length > 0 ? Math.round((evaluatedCount / completedSprints.length) * 100) : 0;
  const scoreDistribution = [1, 2, 3, 4, 5].map((score) => ({
    score: `${score}`, count: scoredSprints.filter((s) => Math.round(s.evaluation_score!) === score).length,
    color: score >= 4 ? '#22C55E' : score >= 3 ? '#F59E0B' : '#EF4444',
  }));

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4"><CheckCircle className="h-5 w-5" /><p className="text-sm font-semibold">Evaluation Coverage</p></div>
        <div className="flex items-center justify-center mb-4">
          <div className="relative h-32 w-32">
            <svg className="h-32 w-32 -rotate-90 transform">
              <circle cx="64" cy="64" r="56" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" />
              <circle cx="64" cy="64" r="56" fill="none" stroke="hsl(var(--primary))" strokeWidth="12"
                strokeDasharray={`${(evaluationRate / 100) * 352} 352`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl font-bold">{evaluationRate}%</span></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="rounded-lg bg-muted p-3">
            <p className="text-xl font-bold text-green-600">{evaluatedCount}</p>
            <p className="text-xs text-muted-foreground">Evaluated</p>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <p className="text-xl font-bold text-yellow-600">{pendingCount}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4"><Star className="h-5 w-5" /><p className="text-sm font-semibold">Score Distribution</p></div>
        {scoreDistribution.some((s) => s.count > 0) ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={scoreDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="score" tickFormatter={(v: string) => `${v}★`} />
              <YAxis allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>{scoreDistribution.map((e) => <Cell key={e.score} fill={e.color} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="flex h-[250px] items-center justify-center text-muted-foreground">No evaluations yet</div>}
      </Card>
    </div>
  );
}

// ── Trends sub-tab ──
function SprintTrendsSection({ sprints }: { sprints: Sprint[] }) {
  // Build 30-day trend from sprint data
  const trendData: { date: string; created: number; completed: number }[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(d); dayEnd.setHours(23, 59, 59, 999);
    trendData.push({
      date: dateStr,
      created: sprints.filter((s) => { const cd = new Date(s.created_at); return cd >= dayStart && cd <= dayEnd; }).length,
      completed: sprints.filter((s) => s.completed_at && (() => { const cd = new Date(s.completed_at!); return cd >= dayStart && cd <= dayEnd; })()).length,
    });
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-4 md:col-span-2">
        <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5" /><p className="text-sm font-semibold">Sprint Creation vs Completion</p></div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="spCreated" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} /><stop offset="95%" stopColor="#7C3AED" stopOpacity={0} /></linearGradient>
              <linearGradient id="spCompleted" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22C55E" stopOpacity={0.3} /><stop offset="95%" stopColor="#22C55E" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <RTooltip /><Legend />
            <Area type="monotone" dataKey="created" name="Created" stroke="#7C3AED" fill="url(#spCreated)" strokeWidth={2} />
            <Area type="monotone" dataKey="completed" name="Completed" stroke="#22C55E" fill="url(#spCompleted)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

// ── Owners sub-tab ──
function SprintOwnersSection({ sprints }: { sprints: Sprint[] }) {
  const [search, setSearch] = useState('');
  const ownerMap: Record<string, { name: string; avatar?: string; total: number; completed: number; onTime: number; late: number; avgScore: number; scored: number; totalDays: number }> = {};
  const now = new Date();
  for (const s of sprints) {
    const oid = s.owner?.id ?? s.owner_id;
    if (!oid) continue;
    if (!ownerMap[oid]) ownerMap[oid] = { name: (s.owner as any)?.full_name ?? 'Unknown', avatar: (s.owner as any)?.avatar_url ?? undefined, total: 0, completed: 0, onTime: 0, late: 0, avgScore: 0, scored: 0, totalDays: 0 };
    const o = ownerMap[oid];
    o.total++;
    const days = (new Date(s.end_date).getTime() - new Date(s.start_date).getTime()) / 86400000;
    o.totalDays += days;
    if (s.status === 'completed') {
      o.completed++;
      if (!s.completed_at || new Date(s.completed_at) <= new Date(s.end_date)) o.onTime++;
    }
    if (s.status === 'active' && new Date(s.end_date) < now) o.late++;
    if (s.evaluation_score != null) { o.avgScore += s.evaluation_score; o.scored++; }
  }
  const owners = Object.entries(ownerMap).map(([id, o]) => ({
    id, name: o.name, avatar: o.avatar,
    total: o.total, completed: o.completed,
    completionRate: o.total > 0 ? Math.round((o.completed / o.total) * 100) : 0,
    onTimeRate: o.completed > 0 ? Math.round((o.onTime / o.completed) * 100) : 0,
    avgScore: o.scored > 0 ? o.avgScore / o.scored : 0,
    avgDuration: o.total > 0 ? Math.round(o.totalDays / o.total) : 0,
    late: o.late,
  }));
  const filtered = owners.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));
  const topPerformers = [...owners].filter((o) => o.completed > 0).sort((a, b) => b.onTimeRate - a.onTimeRate).slice(0, 3);
  const needsAttention = owners.filter((o) => o.late > 1 || (o.onTimeRate < 50 && o.completed > 0)).slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3"><Trophy className="h-5 w-5 text-yellow-500" /><p className="text-sm font-semibold">Top Performers</p></div>
          {topPerformers.length > 0 ? (
            <div className="space-y-2">{topPerformers.map((o, i) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2"><span className="text-lg font-bold text-muted-foreground">{i + 1}</span><UserAvatar name={o.name} avatarUrl={o.avatar} size="xs" /><span className="font-medium text-sm">{o.name}</span></div>
                <div className="flex items-center gap-2">
                  <span className={cn('text-xs font-medium rounded px-1.5 py-0.5', o.onTimeRate >= 80 ? 'bg-green-100 text-green-800' : o.onTimeRate >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}>{o.onTimeRate}% on-time</span>
                  {o.avgScore > 0 && <span className="flex items-center gap-0.5 text-sm"><Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />{o.avgScore.toFixed(1)}</span>}
                </div>
              </div>
            ))}</div>
          ) : <p className="py-8 text-center text-sm text-muted-foreground">No completed sprints yet</p>}
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-yellow-500" /><p className="text-sm font-semibold">Needs Attention</p></div>
          {needsAttention.length > 0 ? (
            <div className="space-y-2">{needsAttention.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <div className="flex items-center gap-2"><UserAvatar name={o.name} avatarUrl={o.avatar} size="xs" /><span className="font-medium text-sm">{o.name}</span></div>
                <div className="flex items-center gap-1.5">
                  {o.late > 0 && <span className="text-xs font-medium rounded px-1.5 py-0.5 bg-red-100 text-red-800">{o.late} late</span>}
                  {o.onTimeRate < 50 && o.completed > 0 && <span className="text-xs font-medium rounded px-1.5 py-0.5 bg-yellow-100 text-yellow-800">{o.onTimeRate}% on-time</span>}
                </div>
              </div>
            ))}</div>
          ) : <p className="py-8 text-center text-sm text-muted-foreground">All owners performing well</p>}
        </Card>
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">All Sprint Owners</p>
          <div className="relative w-56"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input placeholder="Search owners..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" /></div>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Owner</th><th className="px-3 py-2 font-medium">Sprints</th><th className="px-3 py-2 font-medium">Completion</th><th className="px-3 py-2 font-medium">On-Time</th><th className="px-3 py-2 font-medium">Avg Score</th><th className="px-3 py-2 font-medium">Avg Duration</th><th className="px-3 py-2 font-medium">Late</th>
            </tr></thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((o) => (
                <tr key={o.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2"><div className="flex items-center gap-2"><UserAvatar name={o.name} avatarUrl={o.avatar} size="xs" /><span className="font-medium">{o.name}</span></div></td>
                  <td className="px-3 py-2">{o.completed}/{o.total}</td>
                  <td className="px-3 py-2"><div className="flex items-center gap-2"><ProgressBar value={o.completionRate} size="sm" showLabel={false} className="w-16" /><span>{o.completionRate}%</span></div></td>
                  <td className="px-3 py-2"><span className={cn('text-xs font-medium rounded px-1.5 py-0.5', o.onTimeRate >= 80 ? 'bg-green-100 text-green-800' : o.onTimeRate >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}>{o.onTimeRate}%</span></td>
                  <td className="px-3 py-2">{o.avgScore > 0 ? <span className="flex items-center gap-0.5"><Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />{o.avgScore.toFixed(1)}</span> : '—'}</td>
                  <td className="px-3 py-2">{o.avgDuration}d</td>
                  <td className="px-3 py-2">{o.late > 0 ? <span className="text-xs font-medium rounded px-1.5 py-0.5 bg-red-100 text-red-800">{o.late}</span> : <span className="text-muted-foreground">—</span>}</td>
                </tr>
              )) : <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No owners found</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ===========================================================================
// TAB 5 — Teams List
// ===========================================================================
function TeamsListTab() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const fromTab = sp.get('tab') || 'teams-list';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const user = useAuthStore((s) => s.user);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTeam, setDeleteTeam] = useState<Team | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['work-teams'],
    queryFn: () => teamService.list({ limit: 100 }).then((r) => r.data),
  });
  const rawTeams = data?.data ?? [];
  const teams = search
    ? rawTeams.filter(
        (t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.reference_id?.toLowerCase().includes(search.toLowerCase()),
      )
    : rawTeams;

  const createMut = useMutation({
    mutationFn: (d: { name: string; description?: string }) => teamService.create(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-teams'] });
      toast({ title: 'Team created' });
      setCreateOpen(false);
      setCreateName('');
      setCreateDesc('');
    },
    onError: (e: any) =>
      toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const editMut = useMutation({
    mutationFn: (d: { id: string; name: string; description?: string }) =>
      teamService.update(d.id, { name: d.name, description: d.description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-teams'] });
      toast({ title: 'Team updated' });
      setEditOpen(false);
      setEditTeam(null);
    },
    onError: (e: any) =>
      toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => teamService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-teams'] });
      toast({ title: 'Team deleted' });
      setDeleteOpen(false);
      setDeleteTeam(null);
    },
    onError: (e: any) =>
      toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const toggleMut = useMutation({
    mutationFn: (t: Team) => teamService.update(t.id, { is_active: !t.is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-teams'] });
      toast({ title: 'Team status updated' });
    },
    onError: (e: any) =>
      toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function openEdit(team: Team) {
    setEditTeam(team);
    setEditName(team.name);
    setEditDesc(team.description ?? '');
    setEditOpen(true);
  }

  function openDelete(team: Team) {
    setDeleteTeam(team);
    setDeleteOpen(true);
  }

  const canManage = (team: Team) =>
    user?.id === team.owner_id || user?.app_role === 'admin' || user?.app_role === 'owner';

  return (
    <div className="space-y-4">
      {/* Toolbar: search + create */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search teams..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Create Team
        </Button>
      </div>

      {/* Team cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-36 rounded-lg" />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <EmptyState icon={Users} title="No teams yet" description="Create a team to organize your workspace members." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card
              key={team.id}
              className={cn('p-4 space-y-2 cursor-pointer hover:shadow-md transition-shadow', !team.is_active && 'opacity-60')}
              onClick={() => navigate(`/teams/${team.id}`, { state: { fromTab } })}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                    {team.reference_id}
                  </span>
                  {!team.is_active && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">Inactive</span>}
                </div>
                {canManage(team) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => openEdit(team)}>
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleMut.mutate(team)}>
                        {team.is_active ? <><PowerOff className="mr-2 h-3.5 w-3.5" /> Deactivate</> : <><Power className="mr-2 h-3.5 w-3.5" /> Activate</>}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => openDelete(team)}>
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <p className="text-sm font-semibold">{team.name}</p>
              {team.owner?.full_name && (
                <p className="text-xs text-muted-foreground">Owner: {team.owner.full_name}</p>
              )}
              {team.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{team.description}</p>
              )}
              <div className="flex items-center gap-4 pt-1 border-t text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {team.members_count ?? 0} members
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {team.task_count ?? 0} tasks
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(team.created_at).toLocaleDateString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>Add a new team to your workspace.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!createName.trim()) return;
              createMut.mutate({ name: createName.trim(), description: createDesc.trim() || undefined });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="team-name">Name</Label>
              <Input id="team-name" value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Team name" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-desc">Description</Label>
              <Input id="team-desc" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} placeholder="Optional description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMut.isPending || !createName.trim()}>
                {createMut.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>Update team details.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!editTeam || !editName.trim()) return;
              editMut.mutate({ id: editTeam.id, name: editName.trim(), description: editDesc.trim() || undefined });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-team-name">Name</Label>
              <Input id="edit-team-name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Team name" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-team-desc">Description</Label>
              <Input id="edit-team-desc" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Optional description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={editMut.isPending || !editName.trim()}>
                {editMut.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteTeam?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteTeam && deleteMut.mutate(deleteTeam.id)}
            >
              {deleteMut.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// Teams Dashboard Tab
// ===========================================================================
function TeamsDashboardTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['work-teams-all'],
    queryFn: () => teamService.list({ limit: 100 }).then((r) => r.data.data),
  });
  const teams: Team[] = data ?? [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No teams in this workspace"
        description="Create a team from the Teams List tab to see analytics here."
      />
    );
  }

  const totalTeams = teams.length;
  const totalMembers = teams.reduce((s, t) => s + (t.members_count ?? 0), 0);
  const totalTasks = teams.reduce((s, t) => s + (t.task_count ?? 0), 0);
  const memberChartData = teams
    .map((t) => ({ name: t.name.length > 15 ? t.name.slice(0, 15) + '...' : t.name, members: t.members_count ?? 0 }))
    .sort((a, b) => b.members - a.members)
    .slice(0, 10);

  const taskChartData = teams
    .map((t) => ({ name: t.name.length > 15 ? t.name.slice(0, 15) + '...' : t.name, tasks: t.task_count ?? 0 }))
    .sort((a, b) => b.tasks - a.tasks)
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">Total Teams</p>
              <p className="text-2xl font-bold">{totalTeams}</p>
            </div>
            <div className="rounded-full p-2.5 bg-blue-600/10">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">Total Members</p>
              <p className="text-2xl font-bold">{totalMembers}</p>
            </div>
            <div className="rounded-full p-2.5 bg-green-600/10">
              <User2 className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">Total Tasks</p>
              <p className="text-2xl font-bold">{totalTasks}</p>
            </div>
            <div className="rounded-full p-2.5 bg-purple-600/10">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">Avg Members/Team</p>
              <p className="text-2xl font-bold">{totalTeams > 0 ? (totalMembers / totalTeams).toFixed(1) : '0'}</p>
            </div>
            <div className="rounded-full p-2.5 bg-amber-600/10">
              <TrendingUp className="h-5 w-5 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Teams by Member Count */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-4">Teams by Member Count</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={memberChartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="members" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Task Distribution by Team */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-4">Task Distribution by Team</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={taskChartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="tasks" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

